import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';
import {
  AddressingType,
  BuildingDTO,
  EntrancePlantDetailDTO,
  ApartmentDTO,
  BuildingImportDto,
} from '../../../../../client-generator/generated-clients/plants';
import ResidentItem from './ResidentItem/ResidentItem.vue';
import {
  SET_APP_LOADING,
  SET_SELECTED_ENTRANCE_PLANT,
} from '@/core/store/action-types';
import plantsClient from '@/core/services/plants';
import DialogImportFile from './DialogImportFile/DialogImportFile.vue';
import DialogMassiveInviteResidents from './DialogMassiveInviteResidents/DialogMassiveInviteResidents.vue';
import { loadRisersFloorsApartments, processCsvData, SingleLabelApartment } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import BuildingSelector from '../EntrancePlantBadges/DialogAddBadge/BuildingSelector/BuildingSelector.vue';
import ApartmentSelector from '../EntrancePlantBadges/DialogAddBadge/ApartmentSelector/ApartmentSelector.vue';
import ResidentDetailsEdit from './ResidentDetailsEdit/ResidentDetailsEdit.vue';

@Options({
  name: 'EntrancePlantResidents',
  computed: {
    ...mapState(['currentEntrancePlant']),
    backToText() {
      if (this.isBRFA && this.step != 0) {
        return this.$t('AppResourceEP.Buildings');
      } else {
        return this.currentEntrancePlant.name;
      }
    },
    backLink() {
      if (
        this.$route.name === 'page-entranceplant-residents' &&
        !this.selectedBuilding
      ) {
        // Se sei sulla pagina dei building e non hai selezionato alcun edificio, torna alla pagina precedente
        return {
          name: 'page-entranceplant-dashboard',
          params: this.$route.params,
        };
      } else {
        // Altrimenti, torna alla pagina dei building
        return {
          name: 'page-entranceplant-residents',
          params: this.$route.params,
        };
      }
    },
  },
  components: {
    DialogImportFile,
    ResidentItem,
    DialogMassiveInviteResidents,
    BuildingSelector,
    ApartmentSelector,
    ResidentDetailsEdit,
  },
  emits: ['update-list'],
})
export default class EntrancePlantResidents extends Vue {
  apartmentSelected: ApartmentDTO | null = null;
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisibleDialogImportFile: boolean = false;
  importedData: SingleLabelApartment[] = [];
  changingApartment = false;
  isVisibleMassiveInvite = false;
  backToText!: string;
  backLink!: {
    name: string;
    params: any;
  };
  selectedBuilding: BuildingDTO | null = null;
  tabSelectedIndex = 0;
  step = 0;

  get apartments() {
    return this.currentEntrancePlant.buildings![this.tabSelectedIndex]
      .apartments;
  }

  get buildingStructure() {
    if (this.selectedBuilding?.risers === undefined) return [];
    return loadRisersFloorsApartments(this.selectedBuilding);
  }

  get tabOptions() {
    let options: { name: string }[] = [];
    if (this.isBRFA) {
      this.buildingStructure.forEach((value) =>
        options.push({ name: value.riser.name! })
      );
    } else {
      this.currentEntrancePlant.buildings?.forEach((value) =>
        options.push({ name: value.name! })
      );
    }
    return options;
  }

  get isBRFA() {
    return (
      this.currentEntrancePlant.addressingType ===
      AddressingType.BuildingRiserFloorApartment
    );
  }

  get isCallingCode() {
    return (
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_Alphanumeric ||
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_NumbersOnly
    );
  }

  mounted() {
    this.resetState();
    this.handleTabClick({ index: 0 });
  }

  resetState() {
    this.apartmentSelected = null;
    this.selectedBuilding = null;
    this.tabSelectedIndex = 0;
    this.step = 0;
  }

  handleClick() {
    if (this.isBRFA && this.step != 0) {
      this.resetState();
    } else {
      this.$router.push({
        name: 'page-entranceplant-dashboard',
        params: this.$route.params,
      });
    }
  }

  showApartmentDetail(apartment: ApartmentDTO | null) {
    this.changingApartment = true;
    this.apartmentSelected = apartment;

    setTimeout(() => {
      this.changingApartment = false;
    }, 300);
  }

  toggleMassiveInvitation() {
    this.isVisibleMassiveInvite = !this.isVisibleMassiveInvite;
  }

  triggerFilePicker() {
    const input = document.getElementById('fileInput') as HTMLInputElement;
    input.value = '';
    input.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    this.apartmentSelected = null;
    this.$store.dispatch('SET_APP_LOADING', true);
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.errorToast();
      console.error('No file selected!');
      return;
    }

    const file = input.files[0];
    if (!this.fileIsCsv(file)) {
      this.errorToast();
      console.error('File is not a CSV!');
      return;
    }
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      let text = e.target?.result;
      if (typeof text !== 'string') {
        console.error('Failed to read file as text!');
        return;
      } else {
        if (!text.includes(',')) text = text.replaceAll(';', ',');
        const data = await processCsvData(text);
        if (data.length === 0) {
          console.error('Failed to parse CSV data!');
          this.errorToast();
          return;
        }
        this.importedData = data;
        this.isVisibleDialogImportFile = true;
        this.$store.dispatch('SET_APP_LOADING', false);
      }
    };

    reader.onerror = (e) => {
      this.errorToast();
      console.error('Failed to read file!', e);
    };
    reader.readAsText(file); // Legge il contenuto del file come stringa
  }

  errorToast() {
    this.$toast.add({
      severity: 'error',
      summary: this.$t('importFile.error'),
      closable: true,
      life: 6000,
    });
  }

  fileIsCsv(file: File) {
    return (
      (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') &&
      file.name.endsWith('.csv')
    );
  }

  handleCloseDialog() {
    this.importedData = [];
    this.isVisibleDialogImportFile = false;
  }

  async overwrite(res: BuildingImportDto) {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      this.importedData = [];
      this.isVisibleDialogImportFile = false;
      await plantsClient.import(this.currentEntrancePlant.desktopId!, res);
      const plant = await plantsClient.entranceplantGET(
        this.currentEntrancePlant.desktopId!,
        undefined
      );
      this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT, plant);
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async onExportClick() {
    if (!this.currentEntrancePlant.desktopId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const plant = await plantsClient.csv(
        this.currentEntrancePlant.desktopId,
        undefined
      );
      if (plant.data) {
        const url = window.URL.createObjectURL(new Blob([plant.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename =
          this.currentEntrancePlant.name?.trim().replaceAll(' ', '_') + '.csv';
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode!.removeChild(link);
      }
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleTabClick(event: { index: number }) {
    this.apartmentSelected = null;
    this.tabSelectedIndex = event.index;
  }

  handleUpdateList() {
    this.$emit('update-list');

    const updatedApartment = this.currentEntrancePlant
      .buildings!.flatMap((b) => b.apartments || [])
      .find((a) => a.apartmentId === this.apartmentSelected?.apartmentId);

    if (updatedApartment) {
      this.apartmentSelected = updatedApartment;
    } else {
      this.apartmentSelected = null;
    }
  }

  selectBuilding(e: BuildingDTO) {
    this.selectedBuilding = e;
    this.step = 1;
  }

  selectApartment(e: ApartmentDTO) {
    this.apartmentSelected = e;
  }
}
